// packages/ui-kit/src/primitives/Toolbar.tsx
// C3F v18 — reference: design/system/components/core/Toolbar.{jsx,prompt.md}
// Filter/action bar above a list: 56px min height, hairline below, `sticky`
// pins it to the top of the scroll on --bg-canvas.
// Keeps the pre-v18 header surface (title/subtitle/actions/filters) alongside
// the DS slots (left/right/children) so existing call sites keep working.
import { type ReactNode } from "react";

interface Props {
  /** @deprecated pre-v18 header slot (mono eyebrow). Prefer composing `left`. */
  title?: string;
  /** @deprecated pre-v18 header slot. */
  subtitle?: string;
  /** Right-hand actions (pre-v18 name; same slot as DS `right`). */
  actions?: ReactNode;
  /** @deprecated pre-v18 second row of filter controls. Prefer `left` chips. */
  filters?: ReactNode;
  /** DS slot: chips/filters on the left. `children` is its shorthand. */
  left?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  sticky?: boolean;
  className?: string;
}

export function Toolbar({
  title,
  subtitle,
  actions,
  filters,
  left,
  right,
  children,
  sticky = false,
  className = "",
}: Props) {
  const leftSide = left ?? children;
  const rightSide = right ?? actions;
  return (
    <header
      className={`border-b border-border-subtle ${
        sticky ? "sticky top-0 z-[5] bg-canvas" : ""
      } ${className}`}
    >
      <div className="flex min-h-[56px] flex-col items-stretch justify-between gap-3 py-2.5 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          {(title || subtitle) && (
            <div className="min-w-0">
              {title && <h1 className="text-eyebrow">{title}</h1>}
              {subtitle && <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>}
            </div>
          )}
          {leftSide}
        </div>
        {rightSide && (
          <div className="flex flex-col items-stretch gap-2 sm:flex-shrink-0 sm:flex-row sm:items-center">
            {rightSide}
          </div>
        )}
      </div>
      {filters && (
        // `Input`/`Select` are `w-full`: at full page width that made every
        // filter a ~380px control. Cap each direct child on `sm+` so filters
        // stay compact regardless of how wide the page around the toolbar is —
        // mobile keeps them full-width and stacked (grid-cols-1).
        <div className="grid grid-cols-1 gap-2 pb-2.5 sm:flex sm:flex-wrap sm:items-center sm:[&>*]:max-w-xs">
          {filters}
        </div>
      )}
    </header>
  );
}
